import React, { useState } from 'react'
import { supabase } from '../lib/supabase'

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
)
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
)

export default function Auth() {
  const [mode, setMode] = useState('login') // login | signup | reset
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    if (mode === 'login') {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) setError(error.message)

    } else if (mode === 'signup') {
      const { data, error } = await supabase.auth.signUp({ email, password })
      if (error) {
        setError(error.message)
      } else if (data.user) {
        await supabase.from('profiles').insert({
          id: data.user.id,
          email,
          full_name: name,
        })
        setSuccess('Account created! Check your email and click the confirmation link to get started.')
      }

    } else if (mode === 'reset') {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (error) {
        setError(error.message)
      } else {
        setSuccess('Check your email for a password reset link.')
      }
    }

    setLoading(false)
  }

  const titles = {
    login: 'Welcome Back',
    signup: 'Create Account',
    reset: 'Reset Password',
  }

  const subtitles = {
    login: 'Sign in to your career dashboard',
    signup: 'Set up your career dashboard',
    reset: "Enter your email and we'll send you a reset link",
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: 48, letterSpacing: 6, color: 'var(--gold)', lineHeight: 1 }}>JNM</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', letterSpacing: 3, textTransform: 'uppercase', marginTop: 4 }}>Career Hub</div>
        </div>

        <div className="card">
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: 28, letterSpacing: 2, marginBottom: 4 }}>{titles[mode]}</div>
            <div style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 300 }}>{subtitles[mode]}</div>
          </div>

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <div className="mb-16">
                <label>Full Name</label>
                <input type="text" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} required />
              </div>
            )}

            <div className="mb-16">
              <label>Email</label>
              <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>

            {mode !== 'reset' && (
              <div className="mb-24">
                <label>Password</label>
                <div className="pw-wrapper">
                  <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} style={{ paddingRight: 44 }} />
                  <button type="button" className="pw-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
            )}

            {mode === 'login' && (
              <div style={{ textAlign: 'right', marginTop: -16, marginBottom: 20 }}>
                <button type="button" onClick={() => { setMode('reset'); setError(''); setSuccess('') }}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <div style={{ background: 'rgba(229,57,53,0.1)', border: '1px solid rgba(229,57,53,0.2)', color: '#E53935', padding: '10px 14px', borderRadius: 4, fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{ background: 'rgba(67,160,71,0.1)', border: '1px solid rgba(67,160,71,0.2)', color: '#43A047', padding: '10px 14px', borderRadius: 4, fontSize: 13, marginBottom: 16 }}>
                {success}
              </div>
            )}

            <button type="submit" className="btn btn-primary w-full" style={{ justifyContent: 'center', padding: '13px 20px', fontSize: 14 }} disabled={loading}>
              {loading ? <><span className="btn-spinner" /> Please wait...</> : mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
            {mode === 'reset' ? (
              <>
                Remember it?{' '}
                <button onClick={() => { setMode('login'); setError(''); setSuccess('') }}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Sign in
                </button>
              </>
            ) : mode === 'login' ? (
              <>
                Don't have an account?{' '}
                <button onClick={() => { setMode('signup'); setError(''); setSuccess('') }}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button onClick={() => { setMode('login'); setError(''); setSuccess('') }}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--muted)' }}>
          JNM Football Active Membership
        </div>
      </div>
    </div>
  )
}
